import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import {
    fetchNotifications,
    markNotificationRead,
    deleteNotification,
    Notification,
} from "../../store/slices/notificationsSlice";
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Avatar,
    Grid,
    Link as MuiLink,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Divider,
    Tooltip,
    Chip,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
    NotificationsNone,
    FavoriteBorder,
    Comment,
    PersonAdd,
    GroupAdd,
    MailOutline,
    CheckCircleOutline,
    DeleteOutline,
    MarkEmailReadOutlined,
    Visibility,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";

const Notifications = () => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.user);
    const {
        notifications,
        loading: notificationsLoading,
        error: notificationsError,
        statusLoading,
        actionError,
    } = useAppSelector((state) => state.notifications);

    // Get the count of unread non-message notifications
    const unreadOtherCount = useAppSelector((state) => state.notifications.unreadOtherCount);

    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        if (user?.id) {
            dispatch(fetchNotifications(user.id));
        }
    }, [dispatch, user?.id]);

    useEffect(() => {
        if (isInitialLoad && !notificationsLoading) {
            setIsInitialLoad(false);
        }
    }, [notificationsLoading, isInitialLoad]);

    const handleMarkRead = (notificationId: string) => {
        if (!statusLoading[notificationId]) {
            dispatch(markNotificationRead(notificationId));
        }
    };

    const handleDelete = (notificationId: string) => {
        if (!statusLoading[notificationId]) {
            dispatch(deleteNotification(notificationId));
        }
    };

    const handleNotificationClick = (notification: Notification) => {
        if (notification.status === "unread") {
            handleMarkRead(notification.notificationId);
        }

        switch (notification.type) {
            case "comment":
                navigate(`/newsfeed/${notification.entityId}`);
                break;
            case "like":
                navigate(`/newsfeed/${notification.entityId}`);
                break;
            case "connection_request":
                if (notification.initiatorId) {
                    navigate(`/profile/${notification.initiatorId}`);
                }
                break;
            case "connection_accept":
                if (notification.initiatorId) {
                    navigate(`/profile/${notification.initiatorId}`);
                }
                break;
            case "message":
                navigate(`/messaging`);
                break;
            default:
            // console.log("Unhandled notification type click:", notification.type);
        }
    };

    const getNotificationIcon = (type: Notification["type"]) => {
        switch (type) {
            case "like":
                return <FavoriteBorder color="error" />;
            case "comment":
                return <Comment color="info" />;
            case "connection_request":
                return <PersonAdd color="warning" />;
            case "connection_accept":
                return <GroupAdd color="success" />;
            case "message":
                return <MailOutline color="primary" />;
            default:
                return <NotificationsNone />;
        }
    };

    const renderNotifications = () => {
        if (isInitialLoad && notificationsLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 3 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading notifications...</Typography>
                </Box>
            );
        }

        if (notificationsError) {
            return (
                <Alert severity="error">Error loading notifications: {notificationsError}</Alert>
            );
        }

        // Filter out message notifications
        const nonMessageNotifications = notifications.filter((n) => n.type !== "message");

        if (nonMessageNotifications.length === 0) {
            return (
                <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                    You have no notifications yet.
                </Typography>
            );
        }

        return (
            <List
                sx={{
                    width: "100%",
                    bgcolor: `rgba(var(--card-background-rgb), 0.3)`,
                    color: "var(--text-color)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    borderRadius: 2,
                    boxShadow: "var(--card-shadow)",
                    border: "1px solid var(--border-color)",
                    overflow: "hidden",
                    "& .MuiTypography-root": {
                        textShadow:
                            "0 0 1px rgba(var(--background-color-rgb), var(--background-opacity))",
                    },
                }}
            >
                {nonMessageNotifications.map((notification, index) => {
                    const isLoading = statusLoading[notification.notificationId];
                    const isUnread = notification.status === "unread";
                    let formattedDate = "Unknown date";
                    try {
                        const dateValue =
                            typeof notification.createdAt === "string"
                                ? Date.parse(notification.createdAt)
                                : notification.createdAt;
                        if (!isNaN(dateValue)) {
                            formattedDate = formatDistanceToNow(dateValue, { addSuffix: true });
                        }
                    } catch (e) {
                        console.error("Error formatting date:", e);
                    }

                    const itemError =
                        actionError && actionError.notificationId === notification.notificationId
                            ? actionError.message
                            : null;

                    return (
                        <React.Fragment key={notification.notificationId}>
                            <ListItem
                                alignItems="flex-start"
                                secondaryAction={
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-end",
                                        }}
                                    >
                                        {isUnread && (
                                            <Tooltip title="Mark as read">
                                                <span>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="mark read"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMarkRead(
                                                                notification.notificationId,
                                                            );
                                                        }}
                                                        disabled={isLoading}
                                                        size="small"
                                                        sx={{ mb: 0.5 }}
                                                    >
                                                        {isLoading ? (
                                                            <CircularProgress size={20} />
                                                        ) : (
                                                            <Visibility fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Delete">
                                            <span>
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(notification.notificationId);
                                                    }}
                                                    disabled={isLoading}
                                                    size="small"
                                                >
                                                    {isLoading ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <DeleteOutline fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                }
                                sx={{
                                    bgcolor: isUnread ? "action.hover" : "transparent",
                                    opacity: isUnread ? 1 : 0.7,
                                    cursor: "pointer",
                                    "&:hover": {
                                        bgcolor: "action.selected",
                                    },
                                    pr: 12,
                                    position: "relative",
                                    pb: itemError ? 6 : 2,
                                }}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                                    {notification.initiatorProfilePicture ? (
                                        <Avatar
                                            src={notification.initiatorProfilePicture}
                                            alt={notification.initiatorName || "User"}
                                            sx={{ width: 32, height: 32 }}
                                        />
                                    ) : (
                                        getNotificationIcon(notification.type)
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography component="span" variant="body2">
                                            {notification.initiatorName && (
                                                <Typography
                                                    component="strong"
                                                    variant="body2"
                                                    sx={{ fontWeight: "bold", mr: 0.5 }}
                                                >
                                                    {notification.initiatorName}
                                                </Typography>
                                            )}
                                            {notification.message}
                                        </Typography>
                                    }
                                    secondary={formattedDate}
                                />
                            </ListItem>
                            {itemError && (
                                <Alert
                                    severity="error"
                                    variant="filled"
                                    sx={{
                                        mt: -1,
                                        mb: 1,
                                        mx: 2,
                                        fontSize: "0.8rem",
                                        py: 0.2,
                                    }}
                                >
                                    {itemError}
                                </Alert>
                            )}
                            {index < nonMessageNotifications.length - 1 && (
                                <Divider variant="inset" component="li" />
                            )}
                        </React.Fragment>
                    );
                })}
            </List>
        );
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom component="h1">
                Notifications {unreadOtherCount > 0 && `(${unreadOtherCount})`}
            </Typography>
            <Box>{renderNotifications()}</Box>
        </Container>
    );
};

export default Notifications;
